import { History, FileDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VAT_RATE = 0.15; // SA VAT — used for back-compat on older orders that stored only excl
const fmt = (n) =>
  Number(n || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// Backward-compat: orders submitted before the Excl/Incl split stored `unit_price`
// (which was the excl value). Compute the missing side so both are always displayed.
const unitPrices = (item) => {
  const excl = Number(item.unit_price_excl ?? item.unit_price ?? 0);
  const incl = Number(item.unit_price_incl ?? excl * (1 + VAT_RATE));
  return { excl, incl };
};

const orderTotals = (order) => {
  // Prefer stored totals; fallback to items sum; final fallback to legacy total_amount (excl).
  let excl = Number(order.total_amount_excl ?? NaN);
  let incl = Number(order.total_amount_incl ?? NaN);
  if (!Number.isFinite(excl) || !Number.isFinite(incl)) {
    excl = 0;
    incl = 0;
    for (const it of order.items || []) {
      const { excl: e, incl: i } = unitPrices(it);
      excl += e * it.quantity;
      incl += i * it.quantity;
    }
    if (!excl && !incl && order.total_amount) {
      // Very old: treat stored total as excl
      excl = Number(order.total_amount);
      incl = excl * (1 + VAT_RATE);
    }
  }
  return { excl, incl, vat: incl - excl };
};

const PartnerOrders = ({ orders }) => {
  const downloadPdf = async (orderId) => {
    try {
      const response = await axios.get(`${API}/partner/orders/${orderId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EWSS-Quote-${orderId.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download PDF');
    }
  };

  return (
    <div className="space-y-4" data-testid="partner-orders">
      {orders.length === 0 ? (
        <div className="text-center py-12 text-gray-400" data-testid="partner-orders-empty">
          <History className="mx-auto mb-4 text-gray-600" size={48} />
          No orders yet
        </div>
      ) : (
        orders.map((order) => {
          const totals = orderTotals(order);
          return (
            <div
              key={order.id}
              className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700"
              data-testid={`partner-order-${order.id.slice(0, 8)}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white">Order #{order.id.slice(0, 8)}</h3>
                  <p className="text-gray-400 text-sm">
                    {new Date(order.created_at).toLocaleDateString('en-ZA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      order.status === 'completed'
                        ? 'bg-green-500/20 text-green-400'
                        : order.status === 'shipped'
                        ? 'bg-blue-500/20 text-blue-400'
                        : order.status === 'confirmed'
                        ? 'bg-purple-500/20 text-purple-400'
                        : order.status === 'quoted'
                        ? 'bg-cyan-500/20 text-cyan-400'
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}
                  >
                    {order.status}
                  </span>
                  <button
                    onClick={() => downloadPdf(order.id)}
                    className="px-3 py-1.5 bg-ewss-cyan/10 border border-ewss-cyan/30 text-ewss-cyan text-xs font-semibold rounded-lg hover:bg-ewss-cyan/20 transition-colors flex items-center gap-1.5"
                    data-testid={`partner-order-pdf-${order.id.slice(0, 8)}`}
                  >
                    <FileDown size={14} />
                    Download PDF
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-white font-semibold mb-2">Items:</h4>
                <div className="space-y-2">
                  {order.items.map((item, idx) => {
                    const { excl, incl } = unitPrices(item);
                    return (
                      <div
                        key={idx}
                        className="text-sm bg-ewss-navy/50 p-3 rounded grid grid-cols-12 gap-2"
                      >
                        <div className="col-span-5">
                          <span className="text-white font-medium">{item.product_name}</span>
                          <span className="text-gray-400 ml-2 text-xs">({item.sku})</span>
                        </div>
                        <div className="col-span-7 grid grid-cols-2 gap-x-4 text-right">
                          <div>
                            <div className="text-xs text-gray-400">Unit excl / incl</div>
                            <div className="text-white">
                              R {fmt(excl)} <span className="text-gray-500">/</span>{' '}
                              <span className="text-ewss-cyan">R {fmt(incl)}</span>
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-400">
                              {item.quantity} × — Line excl / incl
                            </div>
                            <div className="text-white">
                              R {fmt(excl * item.quantity)}{' '}
                              <span className="text-gray-500">/</span>{' '}
                              <span className="text-ewss-cyan font-semibold">
                                R {fmt(incl * item.quantity)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Totals — Subtotal / VAT / Total */}
              <div className="border-t border-gray-700 pt-3 space-y-1">
                <div className="flex justify-between text-gray-300 text-sm">
                  <span>Subtotal (excl VAT)</span>
                  <span data-testid="partner-order-subtotal-excl">R {fmt(totals.excl)}</span>
                </div>
                <div className="flex justify-between text-gray-400 text-xs">
                  <span>VAT (15%)</span>
                  <span>R {fmt(totals.vat)}</span>
                </div>
                <div className="flex justify-between items-center pt-1">
                  <span className="text-white font-semibold">Total (incl VAT)</span>
                  <span
                    className="text-2xl font-bold text-ewss-cyan"
                    data-testid="partner-order-total-incl"
                  >
                    R {fmt(totals.incl)}
                  </span>
                </div>
              </div>

              {order.notes && (
                <div className="text-sm border-t border-gray-700 pt-4 mt-3">
                  <span className="text-gray-400">Notes: </span>
                  <span className="text-white">{order.notes}</span>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default PartnerOrders;
